import { useEffect, useSyncExternalStore } from 'react';
import { Route, Switch, useLocation } from 'wouter';
import { Layout } from './components';
import {
  AboutPage,
  ContactPage,
  HomePage,
  NotFoundPage,
  PrivacyPage,
  ProcessPage,
  ServicesPage,
  TermsPage,
  WorkPage,
} from './pages';
import { site } from './content/site';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: site.name,
  url: site.url,
  founder: { '@type': 'Person', name: site.founder },
  areaServed: { '@type': 'State', name: 'Florida' },
  description: site.summary,
  knowsAbout: [
    'Brand identity',
    'Local search optimization',
    'Google Business Profile',
    'Apple Business Connect',
    'Social content creation',
    'Paid advertising and lead generation',
    'Lead follow-up automation and CRM',
    'Email marketing and customer retention',
    'Website conversion optimization',
    'Online booking and payments',
    'Business website design',
    'Shopify setup',
    'WooCommerce setup',
    'Retail e-commerce implementation',
    'Newsletter management',
    'Website maintenance',
    'Domain management',
  ],
};

function subscribeToHash(onChange: () => void) {
  const events = ['hashchange', 'popstate', 'pushState', 'replaceState'];
  events.forEach((event) => window.addEventListener(event, onChange));
  return () => events.forEach((event) => window.removeEventListener(event, onChange));
}

export default function App() {
  const [location] = useLocation();
  const hash = useSyncExternalStore(subscribeToHash, () => window.location.hash, () => '');

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('whiteibis:pageview', { detail: { path: location } }));
    const analyticsWindow = window as Window & { dataLayer?: Array<Record<string, unknown>> };
    analyticsWindow.dataLayer?.push({ event: 'page_view', page_path: location });
  }, [location]);

  useEffect(() => {
    let targetId = hash.slice(1);
    try { targetId = decodeURIComponent(targetId); } catch { /* Treat malformed fragments literally. */ }
    const target = targetId ? document.getElementById(targetId) : null;
    if (target) target.scrollIntoView({ behavior: 'instant', block: 'start' });
    else window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location, hash]);

  return (
    <Layout>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <Switch>
        <Route path="/" component={HomePage} />
        <Route path="/services" component={ServicesPage} />
        <Route path="/work" component={WorkPage} />
        <Route path="/process" component={ProcessPage} />
        <Route path="/about" component={AboutPage} />
        <Route path="/contact" component={ContactPage} />
        <Route path="/privacy" component={PrivacyPage} />
        <Route path="/terms" component={TermsPage} />
        <Route component={NotFoundPage} />
      </Switch>
    </Layout>
  );
}
